import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Container } from '@shared/ui';

import styles from './LegalArticle.module.css';

interface LegalArticleProps {
  /** Заголовок документа. Немецкий: так его называет закон. */
  title: string;
  /** Редакция из `GET /config` — согласие указывает именно на неё. */
  version?: string | undefined;
  /** Короткое пояснение над текстом. Переводится, в отличие от самого текста. */
  lead?: ReactNode;
  children: ReactNode;
}

/**
 * Оболочка правовой страницы (DESIGN_SYSTEM §7).
 *
 * Три вещи, которые она обязана обеспечить, — требования § 5 DDG и
 * Art. 246a § 4 EGBGB, а не оформительские предпочтения:
 *
 * 1. **читаемость и доступность без входа**: узкая колонка, обычный поток,
 *    никаких аккордеонов и модалок. «Klar und verständlich» — это в том числе
 *    возможность прочитать и распечатать;
 * 2. **пригодность к сохранению**: текст в разметке, а не в картинке, и
 *    печатная таблица стилей не ломает страницу;
 * 3. **редакция на виду**: без неё непонятно, на что указывает данное согласие.
 *
 * Текст документов немецкий и не переводится: договорный язык — немецкий,
 * а вторая редакция на другом языке толкуется против составителя
 * (§ 305c Abs. 2 BGB). Переводится обрамление — заголовки навигации, «Stand»,
 * пояснение для не-немецкой локали.
 */
export function LegalArticle({ title, version, lead, children }: LegalArticleProps) {
  const { t, i18n } = useTranslation();
  const german = (i18n.resolvedLanguage ?? 'de').startsWith('de');

  return (
    <Container width="narrow">
      <article className={styles.article}>
        <header className={styles.header}>
          <h1 className={styles.title}>{title}</h1>
          {version ? <p className={styles.version}>{t('legal.version', { version })}</p> : null}
          {lead ? <div className={styles.lead}>{lead}</div> : null}
          {german ? null : (
            // Не «мы не перевели», а «перевод не заменяет редакцию»: расхождение
            // двух версий толкуется против составителя (§ 305c Abs. 2 BGB).
            <p className={styles.notice}>{t('legal.germanBinding')}</p>
          )}
        </header>

        <div className={styles.prose}>{children}</div>
      </article>
    </Container>
  );
}

/** Блок адреса или формуляра: переносы строк значимы. */
export function LegalAddress({ lines }: { lines: readonly string[] }) {
  return (
    <address className={styles.address}>
      {lines.map((line, index) => (
        // Строки формуляра повторяются (пустые, подчёркивания), поэтому ключом
        // может быть только позиция: она в этом списке и есть тождество.
        <span key={`${String(index)}:${line}`}>{line === '' ? ' ' : line}</span>
      ))}
    </address>
  );
}
